
import MainLayoutInfo, { LayoutType } from '@/data/layout/MainLayoutInfo';
import Constants from '@/utils/Constants';
import VideoListItemLayout from './VideoListItemLayout';

export class VideoListLayoutGrid {
  public rows: number;
  public cols: number;
  constructor(rows: number, cols: number) {
    this.rows = rows;
    this.cols = cols;
  }
}

export default class VideoListLayout {

  private _columnCount = 0;
  private _rowCount = 0;
  private _itemGapH = 10;
  private _itemGapV = 10;
  private _items: Array<VideoListItemLayout> = [];
  private _itemImageFactorH = 0.20;
  private _width: number | string = 0;
  private _height = 0;
  private _itemHeight = 0;
  private _fullWidth = 0;
  private _layoutType = LayoutType.SMALL;
  private _parentWidth: number | undefined;
  private _usePagination: boolean;

  constructor(usePagination: boolean, layoutType: LayoutType, fullWidth: number, rowCount?: number, parentWidth?: number) {

    this._parentWidth = parentWidth;
    this._fullWidth = fullWidth;
    this._usePagination = usePagination;
    this.init(layoutType, fullWidth, rowCount, parentWidth);
  }

  public init(layoutType: LayoutType, fullWidth?: number, rowCount?: number, parentWidth?: number): void {
    
    if(fullWidth) {
      this._fullWidth = fullWidth;
    }
    this._layoutType = layoutType;
    let usableWidth = 0;
    if(parentWidth) {
      this._parentWidth = parentWidth;
    }

    const grid = VideoListLayout.getDefaultColumnAndRowValues(layoutType, rowCount);
    this._rowCount = grid.rows;
    this._columnCount = grid.cols;
    usableWidth = this.getUsableWidth(layoutType);

    const itemWidth = ((usableWidth - 1) - this._itemGapH * (this._columnCount - 1)) / this._columnCount;
    const itemHeight = itemWidth / Constants.videoDefaultAspectRatio + itemWidth * this._itemImageFactorH;
    const height = (this._rowCount * itemHeight) + (this._rowCount - 1 ? (this._rowCount - 1) * this._itemGapV : 0);

    for(let r = 0; r < this._rowCount; ++r) {
      for(let c = 0; c < this._columnCount; ++c) {

        this._items.push(new VideoListItemLayout(
          c * itemWidth  + c * this._itemGapH,
          r * itemHeight + r * this._itemGapV,
          itemWidth,
          itemHeight,
          itemWidth / Constants.videoDefaultAspectRatio
        ));
      }
    }

    this._width = usableWidth;
    this._height = height;
    this._itemHeight = itemHeight;
  }

  public updateRowCount(rowCount: number): void {
    this.init(this._layoutType, this._fullWidth, rowCount);
  }

  public resetRowCount() {
    const rowCount = VideoListLayout.getDefaultColumnAndRowValues(this.layoutType).rows;
    this.init(this._layoutType, this._fullWidth, rowCount);
  }

  public toCss(value: number) {
    return value + 'px';
  }
  public get columnCount(): number {
    return this._columnCount;
  }
  public get rowCount(): number {
    return this._rowCount;
  }
  public get maxItemCount(): number {
    return this._columnCount * this._rowCount;
  }
  public get itemGapH() {
    return this._itemGapH;
  }
  public get itemGapV() {
    return this._itemGapV;
  }
  public get width() {
    return this._width;
  }
  public get height() {
    return this._height;
  }
  public get layoutType() {
    return this._layoutType;
  }
  public getHeight(rowCount: number) {
    return (rowCount * this._itemHeight) + (rowCount - 1 ? (rowCount - 1) * this._itemGapV : 0);
  }
  public getStdHeight(videoCount: number) {
    if(this._layoutType === LayoutType.SMALL || !this._usePagination) {
      return this.getHeightByVideoCount(videoCount);
    }
    return this.getHeight(this.rowCount);
  }
  public getMaxRows(videoCount: number) {
    return Math.min(Math.ceil(videoCount / this._columnCount), this.rowCount);
  }
  public getHeightByVideoCount(videoCount: number) {
    return this.getHeight(this.getMaxRows(videoCount));
  }

  public getItem(index: number) {
    if(index >= this._items.length) {
      return VideoListItemLayout.getDefault();
    }
    return this._items[index];
  }

  private getUsableWidth(layoutType: LayoutType): number {

    const layoutWidth = MainLayoutInfo.getLayoutWidth(layoutType);
    let w = this._fullWidth;
    if(layoutWidth && layoutWidth < this._fullWidth) {
      w = layoutWidth;
    }
    if(this._parentWidth && this._parentWidth < w) {
      w = this._parentWidth;
    }
    return w - MainLayoutInfo.getGlobalMarginH(layoutType); 
  }

  public static getDefaultColumnAndRowValues(layoutType: LayoutType, rowCount?: number): VideoListLayoutGrid {
    
    switch(layoutType) {
      case LayoutType.XLARGE:
        return new VideoListLayoutGrid(rowCount ? rowCount : 2, 4);
      case LayoutType.LARGE:
        return new VideoListLayoutGrid(rowCount ? rowCount : 2, 4);
      case LayoutType.MEDIUM:
        return new VideoListLayoutGrid(rowCount ? rowCount : 2, 3);
      default:
      case LayoutType.SMALL:
        return new VideoListLayoutGrid(rowCount ? rowCount : 4, 2);
    }
  }
}
